import styles from './aside-styles.module.css';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useContext, useState, useEffect, useRef } from 'react';
import { UserContext } from '../../../context';
import { useMenuStore } from '../../../store/useMenuStore';
import { Icon } from '../../../components';
import { Icons } from '../../../components/Icon/Icon';
import { ASIDE_PORTAL_EVENT } from '../../../utils/aside-portal-event';
import UserMenu from '../UserMenu/UserMenu';

interface AsideProps {}

export const MenuPageToShift = [
    '/',
    '/dashboard',
    '/dashboard/compliance',
    '/storage/view',

    '/tax-optimisation',
    // '/workflows',
];
export const MenuPageToOverlayMid = [
    '/deal-taxes',
    '/legal-structures',
    '/legal-structures/expanded',
    '/workflows',
    '/contact',
    '/disclaimer',
    '/glossary',
    // '/tax-review',
    // '/tax-optimisation',
];

const Aside = ({}: AsideProps) => {
    let location = useLocation();
    const [isOpen, setIsOpen] = useMenuStore((state) => [state.isOpen, state.setIsOpen]);
    const [openedSubMenus, setOpenedSubMenus] = useState<Set<string>>(new Set());
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    const [prevLocation, setPrevLocation] = useState<null | string>(location.pathname + location.search);

    const { user } = useContext(UserContext);
    const asideWrapperRef = useRef<HTMLDivElement>(null);

    const [hasPortalContent, setHasPortalContent] = useState(false);
    const [isPlatformExpanded, setIsPlatformExpanded] = useState(false);

    // Helper function to check if navigating between allowed route pairs
    const isNavigatingBetweenAllowedPairs = (prevPath: string, currentPath: string): boolean => {
        const legalStructuresPair = ['/legal-structures', '/legal-structures/expanded'];
        const dealTaxesPair = ['/deal-taxes', '/deal-taxes/expanded'];

        const prevInLegalPair = legalStructuresPair.some((path) => prevPath.startsWith(path));
        const currentInLegalPair = legalStructuresPair.some((path) => currentPath.startsWith(path));
        const prevInDealPair = dealTaxesPair.some((path) => prevPath.startsWith(path));
        const currentInDealPair = dealTaxesPair.some((path) => currentPath.startsWith(path));

        return (prevInLegalPair && currentInLegalPair) || (prevInDealPair && currentInDealPair);
    };

    // Helper function to check if only URL params changed (same pathname)
    const isOnlyParamsChanged = (prevPath: string, currentPath: string): boolean => {
        // Extract pathname without search params
        const prevPathname = prevPath.split('?')[0];
        const currentPathname = currentPath.split('?')[0];
        return prevPathname === currentPathname;
    };

    useEffect(() => {
        setPrevLocation(location.pathname);
    }, [location.pathname]);

    // Handle content clearing and menu state reset based on navigation
    useEffect(() => {
        // const currentLocation = location.pathname + location.search;
        const currentLocation = location.pathname;
        if (!prevLocation || prevLocation === currentLocation) {
            return;
        }

        const prevPathname = prevLocation.split('?')[0];
        const currentPathname = location.pathname;

        const shouldPreserveState =
            isNavigatingBetweenAllowedPairs(prevPathname, currentPathname) ||
            isOnlyParamsChanged(prevLocation, currentLocation);

        if (!shouldPreserveState) {
            // Clear content and reset menu state
            setHasPortalContent(false);
            setIsPlatformExpanded(false);
            // setOpenedSubMenus(new Set());
        }

        if (MenuPageToOverlayMid.includes(location.pathname)) {
        }
    }, [location.pathname, prevLocation]);

    useEffect(() => {
        if (!isOpen) {
            setOpenedSubMenus(new Set());
        }
    }, [isOpen]);

    useEffect(() => {
        const handleResize = () => setScreenHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSubMenu = (menuName: string) => {
        setOpenedSubMenus((prev) => {
            const newSet = new Set(prev);
            const isOpen = newSet.has(menuName);

            if (screenHeight > 768) {
                if (isOpen) {
                    newSet.delete(menuName);
                } else {
                    if (!isOpen) {
                        newSet.add(menuName);
                    }
                }
            } else {
                newSet.clear();
                if (!isOpen) {
                    newSet.add(menuName);
                }
            }
            return newSet;
        });
    };

    useEffect(() => {
        let timeout: any;
        const handlePortalContentChange = (event: CustomEvent<{ hasContent: boolean }>) => {
            if (event.detail.hasContent) {
                clearTimeout(timeout);
                setHasPortalContent(true);
            } else {
                // Buffer the removal of portal content to bridge the mounting gap between pages
                // timeout = setTimeout(() => {
                setHasPortalContent(false);
                // }, 150);
            }
        };
        window.addEventListener(ASIDE_PORTAL_EVENT, handlePortalContentChange as EventListener);
        return () => {
            window.removeEventListener(ASIDE_PORTAL_EVENT, handlePortalContentChange as EventListener);
            clearTimeout(timeout);
        };
    }, []);

    // Close Aside when clicking anywhere outside of it
    useEffect(() => {
        const handleDocumentMouseDown = (event: MouseEvent) => {
            if (!isOpen) return;

            const wrapper = asideWrapperRef.current;
            const target = event.target as Node | null;

            if (!wrapper || !target) return;

            const clickedOutsideAside = !wrapper.contains(target);

            if (clickedOutsideAside) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [isOpen, setIsOpen]);

    return (
        <div
            ref={asideWrapperRef}
            className={`${styles.wrapper} ${isOpen ? styles.open : ''}`}
            onClick={(e) => {
                if (!isOpen) {
                    e.preventDefault();
                    const target = e.target as Node;
                    const userMenuEl = document.getElementById('userMenu');
                    const clickedOutsideUserMenu = !userMenuEl?.contains(target);
                    if (clickedOutsideUserMenu) {
                        setIsOpen(true);
                    }
                }
            }}
        >
            <aside className={styles.aside}>
                <div className={styles.header}>
                    <Link
                        to={'/'}
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpen(!isOpen);
                        }}
                        className={styles.asideLogo}
                    >
                        <img src={'/logo-small.svg'} alt="White Thesis" />
                    </Link>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpen(false);
                        }}
                    >
                        <Icon icon={Icons.menuCollapse} />
                    </button>
                </div>

                <nav>
                    {!user ? (
                        <>
                            <div className={`${styles.menuItem} ${styles.disabled}`}>
                                <div className={styles.menuLabel}>
                                    <Icon icon={Icons.platform} />
                                    <span className={styles.menuText}>Platform</span>
                                </div>
                            </div>
                            <div className={`${styles.menuItem} ${styles.platformMenu}`}>
                                <Link to={'/contact'} className={styles.menuLabel}>
                                    <Icon icon={Icons.contact} />
                                    <span className={styles.menuText}>Contact us</span>
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            {hasPortalContent && (
                                <div
                                    className={`${styles.menuItem} ${styles.platformMenu} ${styles.borderMenu} ${isPlatformExpanded ? styles.collapsed : ''}`}
                                >
                                    <button
                                        className={styles.menuLabel}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsPlatformExpanded(true);
                                        }}
                                    >
                                        <Icon icon={Icons.platform} />
                                        <span className={styles.menuText}>Platform</span>
                                    </button>
                                </div>
                            )}
                            <div
                                className={`${styles.menuItemsContainer} ${hasPortalContent && !isPlatformExpanded ? styles.collapsed : ''}`}
                            >
                                <div className={`${styles.menuItem} ${!user ? styles.disabled : ''}`}>
                                    <button className={styles.menuLabel} onClick={() => toggleSubMenu('knowledgeBase')}>
                                        <Icon icon={Icons.knowledgeBase} />
                                        <span className={styles.menuText}>Knowledge base</span>
                                    </button>
                                    <div
                                        className={`${styles.subMenu} ${openedSubMenus.has('knowledgeBase') ? styles.open : ''}`}
                                    >
                                        <NavLink to={'/legal-structures'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Investment structures</span>
                                        </NavLink>
                                        <NavLink to={'/deal-taxes'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Deal taxes</span>
                                        </NavLink>
                                        <NavLink to={'/tax-optimisation'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Tax optimisation</span>
                                        </NavLink>
                                    </div>
                                </div>
                                <div className={`${styles.menuItem} ${!user ? styles.disabled : ''}`}>
                                    <button className={styles.menuLabel} onClick={() => toggleSubMenu('storage')}>
                                        <Icon icon={Icons.storage} />
                                        <span className={styles.menuText}>Storage</span>
                                    </button>
                                    <div
                                        className={`${styles.subMenu} ${openedSubMenus.has('storage') ? styles.open : ''}`}
                                    >
                                        <NavLink to={'/dashboard?t=storage'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Structure</span>
                                        </NavLink>
                                        <NavLink to={'/dashboard/compliance'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Compliance</span>
                                        </NavLink>
                                    </div>
                                </div>
                                <div className={`${styles.menuItem} ${!user ? styles.disabled : ''}`}>
                                    <button className={styles.menuLabel} onClick={() => toggleSubMenu('library')}>
                                        <Icon icon={Icons.libraryIcon} />
                                        <span className={styles.menuText}>Library</span>
                                    </button>
                                    <div
                                        className={`${styles.subMenu} ${openedSubMenus.has('library') ? styles.open : ''}`}
                                    >
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Global legislation</span>
                                        </NavLink>
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Your vault</span>
                                        </NavLink>
                                        <NavLink to={'/glossary'} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Glossary</span>
                                        </NavLink>
                                    </div>
                                </div>
                                <div className={`${styles.menuItem} ${!user ? styles.disabled : ''}`}>
                                    <button className={styles.menuLabel} onClick={() => toggleSubMenu('workflows')}>
                                        <Icon icon={Icons.workflow} />
                                        <span className={styles.menuText}>Workflows</span>
                                    </button>
                                    <div
                                        className={`${styles.subMenu} ${openedSubMenus.has('workflows') ? styles.open : ''}`}
                                    >
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Save structures</span>
                                        </NavLink>
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Custom workflow</span>
                                        </NavLink>
                                    </div>
                                </div>
                                <div className={`${styles.menuItem} ${!user ? styles.disabled : ''}`}>
                                    <button className={styles.menuLabel} onClick={() => toggleSubMenu('history')}>
                                        <Icon icon={Icons.history} />
                                        <span className={styles.menuText}>History</span>
                                    </button>
                                    <div
                                        className={`${styles.subMenu} ${openedSubMenus.has('history') ? styles.open : ''}`}
                                    >
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Tax for Italy</span>
                                        </NavLink>
                                        <NavLink to={''} className={styles.subMenuItem}>
                                            <span className={styles.menuText}>Legal review Ireland</span>
                                        </NavLink>
                                    </div>
                                </div>
                                {isPlatformExpanded && hasPortalContent && (
                                    <div
                                        className={`${styles.menuItem} ${styles.borderMenuTop} ${!user ? styles.disabled : ''}`}
                                    >
                                        <button
                                            className={styles.menuLabel}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setIsPlatformExpanded(false);
                                                setOpenedSubMenus(new Set());
                                            }}
                                        >
                                            <Icon icon={Icons.currentWorkflow} />
                                            <span className={styles.menuText}>Current workflow</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div
                                className={`${styles.portalContainerOuter} 
                                ${hasPortalContent ? styles.active : ''} 
                                ${hasPortalContent ? styles.active : ''} 
                                ${isPlatformExpanded ? styles.collapsed : ''}`}
                            >
                                <div className={styles.menuItem}>
                                    <div className={`${styles.menuLabel}`}>
                                        <Icon icon={Icons.currentWorkflow} />

                                        <div id={'asideTitle'} className={styles.menuText}></div>
                                    </div>
                                </div>

                                <div
                                    id={'menuAsideContent'}
                                    className={`${styles.portalContainer} 
                                    ${hasPortalContent ? styles.active : ''} 
                                    ${hasPortalContent ? styles.active : ''}
                                    ${isPlatformExpanded ? styles.collapsed : ''}
                                    `}
                                ></div>
                            </div>

                            <div id={'userMenu'} className={`${styles.menuItemBottom} ${styles.userMenu}`}>
                                <UserMenu isAsideOpen={isOpen} />
                            </div>
                        </>
                    )}
                </nav>
            </aside>
        </div>
    );
};

export default Aside;
